import React from 'react'
import { useLocation } from 'react-router-dom'

import ErrorBoundary from '../components/ErrorBoundary'
import RequireAuth from './RequireAuth'
import { ROUTE_PERMISSIONS, VIEW_SCREEN_PERMISSIONS } from './permissions'

/**
 * What every authenticated route is wrapped in (T-018).
 *
 * One component rather than two wrappers repeated forty times, and — more
 * usefully — the permission comes from the route path via a lookup table
 * instead of being typed at each `<Route>`. A route added without an entry in
 * that table is authenticated but ungated, which is the safe direction to fail:
 * the API still refuses whatever the user is not allowed to do.
 *
 * Two tables are consulted, not one. VIEW_SCREEN_PERMISSIONS holds the
 * order/customer/return detail screens, which used to be deliberately ungated
 * on the grounds that they are only reached from a list that is already gated.
 * They are now gated — a deep link or a pasted URL reaches them directly, and
 * with per-shop roles the person following that link may hold nothing at all in
 * the shop they happen to be in. Kept in a separate map so the change is
 * legible rather than four new lines in a table of forty.
 *
 * Order matters. RequireAuth is OUTSIDE the boundary so that a redirect is a
 * redirect rather than something the boundary might catch, and the boundary is
 * inside so a screen that throws while rendering shows the error panel with the
 * session still intact.
 */
export default function Protected({ path, children }) {
    const location = useLocation()

    return (
        <RequireAuth permission={ROUTE_PERMISSIONS[path] || VIEW_SCREEN_PERMISSIONS[path]}>
            {/* resetKey clears a previous failure when the user navigates
                away. Without it the boundary stays tripped for the rest of the
                session and every subsequent screen renders the error panel. */}
            <ErrorBoundary resetKey={location.pathname}>
                {children}
            </ErrorBoundary>
        </RequireAuth>
    )
}
